import { useState, useEffect } from 'react';
import { normaliseItems, validateVerticalData } from '../components/Comparator/normalizers';

const cache = {};

/**
 * Universal data loader for finmaple_financial_products_v1.json.
 * Resolves a dot-path (e.g. "bank_accounts.accounts") into the JSON,
 * normalises the per-vertical id field → `.id`, runs per-vertical
 * numeric normalisers (_n fields), and caches the raw JSON.
 *
 * @param {string} dataPath   Dot-path into JSON (e.g. "bank_accounts.accounts")
 * @param {string} idField    Source id field name (e.g. "account_id")
 * @param {string} verticalKey  Config key for normaliser lookup (e.g. "bank_accounts")
 */
export default function useProductData(dataPath, idField, verticalKey) {
  const [items, setItems] = useState([]);
  const [rawSection, setRawSection] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        let json;
        if (cache.__root) {
          json = cache.__root;
        } else {
          const res = await fetch('/finmaple_financial_products_v1.json');
          json = await res.json();
          cache.__root = json;
        }

        // walk the dot-path
        const parts = dataPath.split('.');
        let node = json;
        // keep a reference to the top-level section (e.g. json.bank_accounts)
        const section = json[parts[0]] || null;
        for (const p of parts) {
          node = node?.[p];
        }

        if (!cancelled) {
          const arr = Array.isArray(node) ? node : [];

          // 1) Normalise id
          const withId = arr.map((item) => ({
            ...item,
            id: item[idField] || item.id || item.provider_id || item.account_id || item.mortgage_id || item.insurance_id,
          }));

          // 2) Run per-vertical numeric normalisers
          const normalised = verticalKey
            ? normaliseItems(verticalKey, withId)
            : withId;

          // 3) Dev-only validation
          if (verticalKey) {
            validateVerticalData(verticalKey, normalised);
          }

          setItems(normalised);
          setRawSection(section);
          setLoading(false);
        }
      } catch (err) {
        console.error('[useProductData]', err);
        if (!cancelled) {
          setItems([]);
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [dataPath, idField, verticalKey]);

  return { items, rawSection, loading };
}
